/**
 * Country Selector Component
 * Allows users to switch between supported countries
 * Uses flat flag images for better visual appearance
 */
import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, Globe } from 'lucide-react';
import { useCountry } from '../context/CountryContext';

// Flat flag image URLs (using flagcdn.com for high-quality flat flags)
const FLAG_IMAGES = {
  SL: 'https://flagcdn.com/w40/lk.png',  // Sri Lanka
  UK: 'https://flagcdn.com/w40/gb.png',  // United Kingdom
  EU: 'https://flagcdn.com/w40/eu.png',  // European Union
};

// Flag component for consistent rendering
const FlagImage = ({ countryCode, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-5 h-3.5',      // Small - for dropdowns
    md: 'w-7 h-5',        // Medium - for footer
    lg: 'w-8 h-5.5',      // Large - for header
  };
  
  const flagUrl = FLAG_IMAGES[countryCode] || FLAG_IMAGES.SL;
  
  return (
    <img 
      src={flagUrl} 
      alt={`${countryCode} flag`}
      className={`${sizeClasses[size]} rounded-sm object-cover ${className}`}
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
    />
  );
};

const CountrySelector = ({ 
  variant = 'default', // 'default', 'compact', 'badge', 'headerCompact', 'footer'
  className = '',
  showLabel = true 
}) => {
  const { country, countryConfig, setCountry, allCountries, loading } = useCountry();

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  // Header Compact variant - Large flat flag + single arrow only (no text)
  if (variant === 'headerCompact') {
    return (
      <Select value={country} onValueChange={setCountry}>
        <SelectTrigger 
          className={`w-auto border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-2.5 py-1.5 h-auto gap-2 focus:ring-0 focus:ring-offset-0 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:text-gray-400 ${className}`}
          data-testid="header-country-selector"
        >
          <FlagImage countryCode={country} size="lg" />
        </SelectTrigger>
        <SelectContent>
          {allCountries.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-3">
                <FlagImage countryCode={c.code} size="md" />
                <span className="font-medium">{c.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Footer variant - Flat flag + space + Full Country Name
  if (variant === 'footer') {
    return (
      <Select value={country} onValueChange={setCountry}>
        <SelectTrigger 
          className={`w-full border border-gray-600 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg px-3 py-2.5 h-auto focus:ring-1 focus:ring-gray-500 [&>svg]:text-gray-400 ${className}`}
          data-testid="footer-country-selector"
        >
          <span className="flex items-center gap-3">
            <FlagImage countryCode={country} size="md" />
            <span>{countryConfig.name}</span>
          </span>
        </SelectTrigger>
        <SelectContent className="bg-gray-800 border-gray-600">
          {allCountries.map((c) => (
            <SelectItem 
              key={c.code} 
              value={c.code}
              className="text-gray-200 focus:bg-gray-700 focus:text-white"
            >
              <span className="flex items-center gap-3">
                <FlagImage countryCode={c.code} size="md" />
                <span>{c.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Badge variant - minimal display
  if (variant === 'badge') {
    return (
      <Select value={country} onValueChange={setCountry}>
        <SelectTrigger className={`w-auto border-0 bg-transparent p-0 h-auto ${className}`}>
          <Badge variant="outline" className="cursor-pointer hover:bg-gray-100">
            <FlagImage countryCode={country} size="sm" className="mr-1.5" />
            {countryConfig.code}
          </Badge>
        </SelectTrigger>
        <SelectContent>
          {allCountries.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <FlagImage countryCode={c.code} size="sm" />
                <span>{c.name}</span>
                <span className="text-gray-400">({c.currency_symbol})</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <Select value={country} onValueChange={setCountry}>
        <SelectTrigger className={`w-32 ${className}`}>
          <SelectValue>
            <span className="flex items-center gap-1">
              <span>{countryConfig.flag}</span>
              <span>{countryConfig.code}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {allCountries.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <span>{c.code}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Default variant - full display
  return (
    <div className={`space-y-1 ${className}`}>
      {showLabel && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Globe className="h-4 w-4" />
          Country/Region
        </label>
      )}
      <Select value={country} onValueChange={setCountry}>
        <SelectTrigger className="w-full">
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{countryConfig.flag}</span>
              <span>{countryConfig.name}</span>
              <span className="text-gray-400 text-xs">
                ({countryConfig.currency_symbol}, {countryConfig.mileage_unit})
              </span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {allCountries.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <span className="font-medium">{c.name}</span>
                <span className="text-gray-400 text-xs">
                  ({c.currency_symbol}, {c.distance_unit})
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CountrySelector;
